import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { formatMoney, formatSignedMoney } from '../formatters';

function QuoteSummary({
  builder,
  bundleBuilderSendSummary,
  copyStatus,
  isAgentBeta,
  mobilityDiscountActive,
  offerEmailPreview,
  onCopyQuote,
  onPrepareSend,
  onReset,
  quote,
  quoteItemsBySection,
  quoteSectionLabels,
  renderInternetPriceBreakdown,
  renderTvPriceBreakdown,
  savingsStyle,
  selectedTv,
}) {
  return (
<Grid item xs={12} xl={4}>
    <Paper
        sx={{
            p: { xs: 2, sm: 2.5 },
            position: { xl: 'sticky' },
            top: { xl: 88 }
        }}
    >
        <Stack spacing={2}>
            <Box>
                <Typography variant="h5">Quote Summary</Typography>
                <Typography variant="body2" color="text.secondary">
                    {isAgentBeta
                        ? 'Review selected products. Customer email send includes Home Services only.'
                        : 'Static prototype pricing. Verify against the active DOCX before publishing.'}
                </Typography>
            </Box>

            <Divider />

            {quote.items.length === 0 ? (
                <Box sx={{ py: 1 }}>
                    <Typography variant="subtitle2">No products selected</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Choose products to build a quote.
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={1.5}>
                    {quoteItemsBySection.map((group) => (
                        <Box key={group.section}>
                            <Chip label={quoteSectionLabels[group.section] || group.section} size="small" color="primary" variant="outlined" sx={{ mb: 0.75 }} />
                            <List dense disablePadding>
                                {group.items.map((item) => {
                                    const isInternetItem = item.section === 'Internet';
                                    const isSelectedTvItem = item.section === 'TV' && selectedTv && item.label === selectedTv.name;

                                    return (
                                        <React.Fragment key={`${item.section}-${item.label}-${item.detail}`}>
                                            <ListItem
                                                disableGutters
                                                sx={{ alignItems: 'flex-start', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
                                            >
                                                <ListItemText
                                                    primary={item.label}
                                                    secondary={item.detail}
                                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 700 }}
                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                    sx={{ pr: 1 }}
                                                />
                                                <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap', pt: 0.25 }}>
                                                    {formatMoney(item.amount)}
                                                </Typography>
                                            </ListItem>
                                            {isInternetItem && renderInternetPriceBreakdown()}
                                            {isSelectedTvItem && renderTvPriceBreakdown()}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </Box>
                    ))}
                </Stack>
            )}

            {quote.tvSelectionSummary && (
                <Accordion disableGutters variant="outlined" sx={{ boxShadow: 'none' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 42, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Chip label="TV Selection Summary" size="small" color="secondary" variant="outlined" />
                            <Typography variant="body2" color="text.secondary">
                                {quote.tvSelectionSummary.bestValuePackage}
                            </Typography>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        <Stack spacing={1}>
                            {quote.tvSelectionSummary.selectedChoices?.length > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Customer selected</Typography>
                                    <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap sx={{ mt: 0.5 }}>
                                        {quote.tvSelectionSummary.selectedChoices.map((choice) => (
                                            <Chip key={choice} label={choice} size="small" variant="outlined" />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                            {(quote.tvSelectionSummary.requiredThemePacks?.length > 0 || quote.tvSelectionSummary.premiumChannels?.length > 0) && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Recommendation inputs</Typography>
                                    <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap sx={{ mt: 0.5 }}>
                                        {quote.tvSelectionSummary.requiredThemePacks.map((pack) => (
                                            <Chip key={`theme-${pack}`} label={pack} size="small" color="primary" variant="outlined" />
                                        ))}
                                        {quote.tvSelectionSummary.premiumChannels.map((channel) => (
                                            <Chip key={`premium-${channel}`} label={channel} size="small" color="secondary" variant="outlined" />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                            <Typography variant="body2">
                                <strong>Best value:</strong> {quote.tvSelectionSummary.bestValuePackage}
                            </Typography>
                            {quote.tvSelectionSummary.addOnCount > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    Add-on: {quote.tvSelectionSummary.addOnCount} extra theme pack{quote.tvSelectionSummary.addOnCount === 1 ? '' : 's'} at {formatMoney(quote.tvSelectionSummary.addonUnitPrice)}/mo each ({formatMoney(quote.tvSelectionSummary.addOnTotal)}/mo)
                                </Typography>
                            )}
                            {quote.tvSelectionSummary.extraValueNote && (
                                <Typography variant="body2" color="text.secondary">
                                    {quote.tvSelectionSummary.extraValueNote.replace(/^Note:\s*/i, '')}
                                </Typography>
                            )}
                            {quote.tvSelectionSummary.upgradeOpportunity && (
                                <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                                    <Typography variant="body2">
                                        For {formatMoney(quote.tvSelectionSummary.upgradeOpportunity.priceDiff)}/mo more: {quote.tvSelectionSummary.upgradeOpportunity.packageName}
                                        {[
                                            quote.tvSelectionSummary.upgradeOpportunity.extraThemes > 0 ? `${quote.tvSelectionSummary.upgradeOpportunity.extraThemes} extra theme pack${quote.tvSelectionSummary.upgradeOpportunity.extraThemes === 1 ? '' : 's'}` : null,
                                            quote.tvSelectionSummary.upgradeOpportunity.extraPremiums > 0 ? `${quote.tvSelectionSummary.upgradeOpportunity.extraPremiums} extra premium channel${quote.tvSelectionSummary.upgradeOpportunity.extraPremiums === 1 ? '' : 's'}` : null
                                        ].filter(Boolean).length ? `, includes ${[
                                            quote.tvSelectionSummary.upgradeOpportunity.extraThemes > 0 ? `${quote.tvSelectionSummary.upgradeOpportunity.extraThemes} extra theme pack${quote.tvSelectionSummary.upgradeOpportunity.extraThemes === 1 ? '' : 's'}` : null,
                                            quote.tvSelectionSummary.upgradeOpportunity.extraPremiums > 0 ? `${quote.tvSelectionSummary.upgradeOpportunity.extraPremiums} extra premium channel${quote.tvSelectionSummary.upgradeOpportunity.extraPremiums === 1 ? '' : 's'}` : null
                                        ].filter(Boolean).join(' and ')}` : ''}
                                    </Typography>
                                </Alert>
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}

            <Box>
                <Chip label="Discounts & Credits" size="small" color="success" variant="outlined" sx={{ mb: 0.75 }} />
                <Stack spacing={0.75}>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
                        <Chip
                            label={builder.pabIncluded ? 'PAB included' : 'PAB not included'}
                            size="small"
                            color={builder.pabIncluded ? 'success' : 'warning'}
                            variant="outlined"
                        />
                        {mobilityDiscountActive && (
                            <Chip
                                label="TELUS/Koodo mobility discount active"
                                size="small"
                                color="success"
                                variant="outlined"
                            />
                        )}
                        {builder.eppEnabled && (
                            <Chip
                                label="EPP closing tool active"
                                size="small"
                                color="success"
                                variant="outlined"
                            />
                        )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        One-time bill credit: {quote.billCredit ? formatMoney(quote.billCredit) : 'Not applied'}
                    </Typography>
                </Stack>
            </Box>

            {quote.savings && (
                <Box>
                    <Chip label="Savings Comparison" size="small" color={savingsStyle.color} variant="outlined" sx={{ mb: 0.75 }} />
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 1.5,
                            bgcolor: savingsStyle.bgcolor,
                            borderColor: savingsStyle.borderColor
                        }}
                    >
                        <Stack spacing={0.75}>
                            <Typography variant="subtitle2" color={`${savingsStyle.color}.main`}>
                                {savingsStyle.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Current bill: {formatMoney(quote.savings.currentMonthlyBill)}/mo
                            </Typography>
                            {quote.savings.status === 'saves' && (
                                <Typography variant="body2">
                                    Estimated 2-year savings: {formatMoney(quote.savings.twoYearDifference)}
                                </Typography>
                            )}
                            {quote.savings.status === 'similar' && (
                                <Typography variant="body2">
                                    Focus on fibre upgrade, price lock, and included bundle value.
                                </Typography>
                            )}
                            {quote.savings.status === 'more' && (
                                <>
                                    <Typography variant="body2">
                                        Estimated 2-year difference: {formatMoney(Math.abs(quote.savings.twoYearDifference))}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        If customer is on a temporary promo, ask when their promo ends. Compare against future regular price, fibre upgrade, upload speed, and price lock.
                                    </Typography>
                                </>
                            )}
                            {quote.billCredit > 0 && (
                                <Typography variant="body2" fontWeight={700}>
                                    2-year impact including bill credit: {formatSignedMoney(quote.savings.twoYearImpactWithCredit)}
                                </Typography>
                            )}
                        </Stack>
                    </Paper>
                </Box>
            )}

            <Divider />

            {isAgentBeta && quote.quoteOnlySubtotal > 0 ? (
                <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', p: 1.5, borderRadius: 1 }}>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>Home Services email total</Typography>
                            <Typography variant="caption" color="text.secondary">Internet, TV, and Home Phone only</Typography>
                        </Box>
                        <Typography variant="h5" color="primary.main">
                            {formatMoney(quote.homeServicesSubtotal)}
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ bgcolor: 'rgba(46, 125, 50, 0.08)', p: 1.5, borderRadius: 1 }}>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>Full quote total</Typography>
                            <Typography variant="caption" color="text.secondary">Includes quote-only / separate-flow items</Typography>
                        </Box>
                        <Typography variant="h4" color="success.main">
                            {formatMoney(quote.monthlySubtotal)}
                        </Typography>
                    </Box>
                </Stack>
            ) : (
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', p: 1.5, borderRadius: 1 }}>
                    <Typography variant="h6">Monthly subtotal</Typography>
                    <Typography variant="h4" color="primary.main">
                        {formatMoney(quote.monthlySubtotal)}
                    </Typography>
                </Box>
            )}

            {isAgentBeta && bundleBuilderSendSummary.unsupportedProducts.length > 0 && offerEmailPreview.hasHomeServices && (
                <Alert severity="warning">
                    Customer email will include Home Services only. Mobility, Security, and Add-ons are quote-only / separate-flow in this beta.
                </Alert>
            )}

            {isAgentBeta && bundleBuilderSendSummary.unsupportedProducts.length > 0 && !offerEmailPreview.hasHomeServices && (
                <Alert severity="warning">
                    No Home Services selected. Customer email send is available for Internet, TV, and Home Phone only.
                </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row', xl: 'column' }} spacing={1}>
                <Button variant="contained" startIcon={<CopyIcon />} onClick={onCopyQuote} fullWidth>
                    Copy Quote
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={onPrepareSend}
                    fullWidth
                    disabled={isAgentBeta && !offerEmailPreview.hasHomeServices}
                >
                    {isAgentBeta ? 'Prepare Send' : 'Preview Offer Email'}
                </Button>
                <Button variant="outlined" startIcon={<ResetIcon />} onClick={onReset} fullWidth>
                    Reset Builder
                </Button>
            </Stack>

            {copyStatus && (
                <Alert severity={copyStatus.startsWith('Quote') ? 'success' : 'warning'}>
                    {copyStatus}
                </Alert>
            )}
        </Stack>
    </Paper>
</Grid>
  );
}

export default QuoteSummary;
